import { useAuth } from '@choco-admin/auth';
import { Button, Card, CardBody } from '@heroui/react';
import React, { useEffect } from 'react';
import { Navigate, useLocation, type Location } from 'react-router-dom';

import authBackground from '../../assets/authBackground.jpg';
import { useTheme } from '../../contexts/ThemeContext';
import { rememberPostLoginRedirect } from '../../utils/postLoginRedirect';

/**
 * LoginPage Component
 *
 * Modern login page with:
 * - Glassmorphism card design
 * - Smooth animations and transitions
 * - Improved visual hierarchy
 * - Modern spacing and typography
 * - @choco-admin/auth library integration
 * - HeroUI components for styling
 * - Responsive design
 * - Accessibility features
 * - Google OAuth integration
 */
export const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, error, signInWithOAuth, clearError } =
    useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const fromLocation = (location.state as { from?: Location } | null)?.from;

  // Clear errors on mount
  useEffect(() => {
    if (error) {
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: runs once on mount to clear any stale auth errors from a previous session
  }, []);

  // Remember where AuthGuard bounced us from, so it can send us back there
  // once the OAuth round trip completes.
  useEffect(() => {
    rememberPostLoginRedirect(fromLocation);
  }, [fromLocation]);

  // Redirect if already authenticated (must be after all hooks). AuthGuard on
  // the target route follows any recorded destination.
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGoogleLogin = async () => {
    try {
      await signInWithOAuth('Google');
      // User will be redirected during OAuth flow
    } catch (err) {
      // Error is handled by the auth context
      // eslint-disable-next-line no-console
      console.error('Login error:', err);
    }
  };

  return (
    <div
      className="relative flex items-center justify-center min-h-screen w-screen bg-cover bg-center bg-no-repeat p-4 sm:p-6"
      style={{ backgroundImage: `url(${authBackground})` }}
    >
      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />

      {/* Main content container */}
      <div className="relative z-10 w-full max-w-md transition-opacity duration-500 ease-out">
        <Card className="bg-content1/95 backdrop-blur-md border-default-200 shadow-2xl">
          <CardBody className="p-8 sm:p-10 space-y-8">
            {/* Header section */}
            <div className="text-center space-y-4">
              <div className="flex justify-center" data-testid="login-title">
                <img
                  src="/choco-logo.svg"
                  alt="Choco Logo"
                  className="h-16 sm:h-20 w-auto object-contain"
                  style={
                    theme === 'dark'
                      ? { filter: 'brightness(0) invert(1)' }
                      : undefined
                  }
                />
              </div>
              <p className="text-default-500 text-sm sm:text-base">
                Sign in to access the admin portal
              </p>
            </div>

            {/* Error message */}
            {error && (
              <Card className="bg-danger-50 border-danger-200 transition-all duration-300">
                <CardBody className="p-4">
                  <div
                    data-testid="error-message"
                    className="text-danger text-sm text-center font-medium"
                  >
                    {error}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Login button */}
            <div className="space-y-4">
              <Button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                data-testid="google-login-button"
                aria-label="Sign in with Google"
                variant="flat"
                size="lg"
                className="w-full !bg-content1 !text-foreground border-2 border-default-200 hover:!bg-default-100 hover:border-default-300 transition-all duration-200 shadow-md hover:shadow-lg"
                startContent={
                  isLoading ? (
                    <div className="w-5 h-5 border-2 border-default-300 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )
                }
              >
                <span className="font-medium">Sign in with Google</span>
              </Button>
            </div>

            {/* Footer text */}
            <div className="text-center pt-4">
              <p className="text-default-400 text-sm italic">
                One admin to rule them all...
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
